/**
 * Year comparison — the whole modelling period, month by month, CURRENT vs
 * MODELLED (Chris, 2026-08-20): running days, turns, locos, carried, costs and
 * profit each side, with the season grand total at the top. A month only
 * contributes a modelled side once its slice is DESIGNED (§6.3 — the roll-up
 * never assumes); the current side comes from the slice's own snapshot, so
 * each month is scored against ITS pattern, not the active one.
 */

export interface YearSideSnap {
  /** Passenger workings per operating day (positioning excluded) — the
   *  day-weighted average over the month's scored days. */
  services: number
  /** [min, max] when the month mixes timetable colours/day-types with
   *  different working counts — shown as a range, never blurred to one number. */
  servicesRange?: [number, number]
  steamN: number
  dieselN: number
  carriedPerDay: number
  revenuePerDay: number
  costsPerDay: number
  /** REAL train-miles per running day (engine per-segment mileage). Absent on
   *  snapshots saved before 2026-09-02 — readers fall back to a 24-mi/turn proxy
   *  and say so. */
  milesPerDay?: number
  /** How many of the month's running days this side could score (gala/diner
   *  days have no standard pattern and are extrapolated at the average). */
  scoredDays?: number
  /** Dates whose like-for-like basis day ran NO standard services — carried
   *  at 0 services / £0 / fixed cost only (Chris, 2026-09-03). Set when the
   *  side has been re-based day for day. */
  noServiceDays?: number
  /** Dates with no like-for-like basis day, shown at the side's average. */
  averageDays?: number
  /** The MONTH's FIXED cost (£): the P&L's fixed lines for that month — the
   *  line runs all year, so a non-running month carries this with nothing else
   *  (Chris, 2026-09-04). Flex sliders applied on the modelled side. Present
   *  once a side is priced on the accounts model. */
  fixedMonth?: number
  /** VARIABLE cost per running day: per running day, per turn, per train-mile,
   *  per passenger. */
  variablePerDay?: number
  /** Passengers a running day TURNED AWAY BY TIMING (Chris, 2026-09-07): people
   *  in the like day's demand who did not travel on this side's timetable — no
   *  train within 75 minutes of their time once the Recapture discount applies,
   *  or the nearest train had no way home (engine.ts Scenario.lostSales).
   *  Written by seasonBuild.ts::foldSide and re-based day for day by yearRows;
   *  absent on snapshots saved before 2026-09-07 (shown as "—"). */
  turnedAwayPerDay?: number
  /** TIMETABLED miles per register engine per running day (key = engine id),
   *  and the share of running days each engine works (0..1) — what the named
   *  hire lines on the Cost model stage are priced on (Chris, 2026-09-11).
   *  Absent without a register or on older snapshots. */
  engineMilesPerDay?: Record<string, number>
  engineDaysPerDay?: Record<string, number>
}

export interface YearRow {
  key: string
  monthNo: number
  monthLabel: string
  dayClass: string
  statusLabel: string
  /** Built and banked — every built month is a designed month; a point where
   *  the Brief bound is listed in `issues`, never a second status. */
  designed: boolean
  /** Where the Brief bound for this month, in words (seasonBuild.ts::MonthIssue). */
  issues?: { kind: string; text: string }[]
  /** Real running days for this slice from the applied running set; null = no set. */
  runningDays: number | null
  cur: YearSideSnap | null
  mod: YearSideSnap | null
  /** True when this is the ACTIVE slice, shown from live state rather than a save. */
  live: boolean
}

/** One row's MONTH figures, one side — THE owner of "a month's money" for the
 *  season table on screen, the workbook Summary and every board-paper table.
 *  A side priced on the accounts model carries `fixedMonth` + `variablePerDay`
 *  (a non-running month: fixed only); an older snapshot carries `costsPerDay`
 *  alone, so its fixed/variable split is unknown and printed as such. */
export interface MonthFigures {
  pax: number; revenue: number; fixed: number | null; variable: number | null; costs: number; profit: number
  /** Passengers turned away by timing over the month; null on a snapshot saved
   *  before the figure existed. */
  turnedAway: number | null
}
export function monthFigures(s: YearSideSnap | null, days: number | null): MonthFigures | null {
  if (!s || days == null) return null
  const pax = s.carriedPerDay * days
  const revenue = s.revenuePerDay * days
  const turnedAway = s.turnedAwayPerDay != null ? s.turnedAwayPerDay * days : null
  if (s.fixedMonth != null) {
    const variable = (s.variablePerDay ?? 0) * days
    const costs = s.fixedMonth + variable
    return { pax, revenue, fixed: s.fixedMonth, variable, costs, profit: revenue - costs, turnedAway }
  }
  const costs = s.costsPerDay * days
  return { pax, revenue, fixed: null, variable: null, costs, profit: revenue - costs, turnedAway }
}

export interface YearTotalsSide {
  revenue: number; costs: number; fixed: number | null; variable: number | null; profit: number; carried: number; maxLocos: number
  /** Passengers turned away by timing over the season; null when any month lacks the figure. */
  turnedAway: number | null
}
export interface YearTotals {
  runningDays: number
  cur: YearTotalsSide | null
  mod: YearTotalsSide | null
  coveredRows: number
  totalRows: number
}

/** Season totals over EVERY month with a side — running or not: a non-running
 *  month contributes its fixed cost and nothing else (the line runs all year).
 *  Fixed/variable totals are null when any month lacks the split. */
export function yearTotals(rows: YearRow[]): YearTotals {
  type Acc = { revenue: number; costs: number; fixed: number | null; variable: number | null; profit: number; carried: number; maxLocos: number; turnedAway: number | null; any: boolean }
  const mk = (): Acc => ({ revenue: 0, costs: 0, fixed: 0, variable: 0, profit: 0, carried: 0, maxLocos: 0, turnedAway: 0, any: false })
  const cur = mk(), mod = mk()
  let runningDays = 0, n = 0
  const add = (acc: Acc, s: YearSideSnap | null, days: number) => {
    const f = monthFigures(s, days)
    if (!f || !s) return
    acc.any = true
    acc.revenue += f.revenue; acc.costs += f.costs; acc.profit += f.profit; acc.carried += f.pax
    acc.maxLocos = Math.max(acc.maxLocos, s.steamN + s.dieselN)
    acc.fixed = f.fixed == null || acc.fixed == null ? null : acc.fixed + f.fixed
    acc.variable = f.variable == null || acc.variable == null ? null : acc.variable + f.variable
    acc.turnedAway = f.turnedAway == null || acc.turnedAway == null ? null : acc.turnedAway + f.turnedAway
  }
  for (const r of rows) {
    if (r.runningDays == null) continue
    if (!r.cur && !r.mod) continue
    runningDays += r.runningDays
    n += 1
    add(cur, r.cur, r.runningDays)
    add(mod, r.mod, r.runningDays)
  }
  const out = (a: Acc): YearTotalsSide | null => a.any ? { revenue: a.revenue, costs: a.costs, fixed: a.fixed, variable: a.variable, profit: a.profit, carried: a.carried, maxLocos: a.maxLocos, turnedAway: a.turnedAway } : null
  return { runningDays, cur: out(cur), mod: out(mod), coveredRows: n, totalRows: rows.length }
}

const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
const gbpK = (n: number) => {
  const a = Math.abs(n)
  if (a >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}m`
  if (a >= 10_000) return `£${(n / 1_000).toFixed(0)}k`
  if (a >= 1_000) return `£${(n / 1_000).toFixed(1)}k`
  return `£${Math.round(n)}`
}
const locoTxt = (s: YearSideSnap) => `${s.steamN}S${s.dieselN > 0 ? `+${s.dieselN}D` : ''}`

export function YearComparePanel({ rows, seasonLabel }: { rows: YearRow[]; seasonLabel: string }) {
  const totals = yearTotals(rows)
  const delta = totals.cur && totals.mod ? totals.mod.profit - totals.cur.profit : null
  const side = (s: YearSideSnap | null, days: number | null, field: 'turns' | 'locos' | 'carried' | 'turnedAway' | 'fixed' | 'variable' | 'profit') => {
    const f = monthFigures(s, days)
    if (!s || !f || days == null) return <td className="num sh-mono sh-dim">—</td>
    const runs = s.services > 0
    const v = field === 'turns' ? (runs ? (s.servicesRange ? `${s.servicesRange[0]}–${s.servicesRange[1]}` : String(s.services)) : (days === 0 ? '—' : '0'))
      : field === 'locos' ? (runs ? locoTxt(s) : '—')
      : field === 'carried' ? num0.format(Math.round(f.pax))
      : field === 'turnedAway' ? (f.turnedAway == null ? '—' : num0.format(Math.round(f.turnedAway)))
      : field === 'fixed' ? (f.fixed == null ? '—' : gbpK(f.fixed))
      : field === 'variable' ? (f.variable == null ? '—' : gbpK(f.variable))
      : gbpK(f.profit)
    const cls = field === 'profit' ? (f.profit >= 0 ? ' sh-delta--good' : ' sh-delta--bad')
      : ((field === 'fixed' || field === 'variable') && f[field] == null) || v === '—' ? ' sh-dim' : ''
    return <td className={`num sh-mono${cls}`}>{v}</td>
  }
  return (
    <section className="sh-card">
      <div className="sh-card-head">
        <span className="sh-card-title">Season by month — current vs modelled · {seasonLabel}</span>
        <span className="sh-card-note">
          {totals.coveredRows < totals.totalRows
            ? `totals cover ${totals.coveredRows} of ${totals.totalRows} months (nothing is assumed for the rest)`
            : 'every month covered · real running-days'}
        </span>
      </div>

      {/* Grand total — the final comparison, at the top. */}
      <div className="sh-compare-heroes" style={{ margin: '10px 12px 4px' }}>
        <div className="sh-chero">
          <div className="sh-chero-label">Current — season total ({totals.runningDays} running days)</div>
          <div className="sh-chero-value sh-mono">{totals.cur ? gbpK(totals.cur.profit) : '—'}</div>
          <div className="sh-chero-sub">
            {totals.cur ? <>profit · rev {gbpK(totals.cur.revenue)} · cost {gbpK(totals.cur.costs)} · {num0.format(Math.round(totals.cur.carried))} carried · up to {totals.cur.maxLocos} locos</> : 'no covered months yet'}
          </div>
        </div>
        <div className="sh-chero sh-chero--modelled">
          <div className="sh-chero-label">Modelled — season total</div>
          <div className="sh-chero-value sh-mono">{totals.mod ? gbpK(totals.mod.profit) : '—'}</div>
          <div className="sh-chero-sub">
            {totals.mod
              ? <>{delta != null ? <>{delta >= 0 ? '▲' : '▼'} {gbpK(Math.abs(delta))} vs current · </> : null}rev {gbpK(totals.mod.revenue)} · cost {gbpK(totals.mod.costs)} · {num0.format(Math.round(totals.mod.carried))} carried · up to {totals.mod.maxLocos} locos</>
              : 'design slices to fill this side'}
          </div>
        </div>
      </div>

      <div className="stu-scroll">
        <table className="stu-table sh-cmp-table">
          <thead>
            <tr>
              <th rowSpan={2}>Month · slice</th>
              <th rowSpan={2} className="num">Run days</th>
              <th colSpan={7} style={{ textAlign: 'center', borderBottom: '1px solid #ddd6c4' }}>Current</th>
              <th colSpan={7} style={{ textAlign: 'center', borderBottom: '1px solid #ddd6c4' }}>Modelled</th>
              <th rowSpan={2} className="num">Δ profit</th>
            </tr>
            <tr>
              <th className="num">Turns/day</th><th className="num">Locos</th><th className="num">Carried</th><th className="num" title="Passengers turned away by timing: no train within 75 minutes of their time once the Recapture discount applies, or the nearest train had no way home. Not in Carried.">Turned away</th><th className="num">Fixed</th><th className="num">Variable</th><th className="num">Profit</th>
              <th className="num">Turns/day</th><th className="num">Locos</th><th className="num">Carried</th><th className="num" title="Passengers turned away by timing: no train within 75 minutes of their time once the Recapture discount applies, or the nearest train had no way home. Not in Carried.">Turned away</th><th className="num">Fixed</th><th className="num">Variable</th><th className="num">Profit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const fc = monthFigures(r.cur, r.runningDays), fm = monthFigures(r.mod, r.runningDays)
              const d = fc && fm ? fm.profit - fc.profit : null
              return (
                <tr key={r.key} className={r.live ? 'sh-row--active' : undefined}>
                  <td>
                    <strong>{r.monthLabel}</strong> · {r.dayClass}
                    {r.live ? <span className="sh-tag" style={{ marginLeft: 6 }}>live</span> : null}
                    {!r.designed && <span className="sh-dim"> — {r.statusLabel.toLowerCase()}</span>}
                    {r.issues && r.issues.length > 0 && (
                      <span className="sh-prov sh-prov--assumed" style={{ marginLeft: 6, whiteSpace: 'normal' }} title={r.issues.map((i) => i.text).join('\n')}>
                        Scope bound: {r.issues.map((i) => i.text).join(' · ')}
                      </span>
                    )}
                  </td>
                  <td className="num sh-mono">{r.runningDays ?? '—'}</td>
                  {side(r.cur, r.runningDays, 'turns')}
                  {side(r.cur, r.runningDays, 'locos')}
                  {side(r.cur, r.runningDays, 'carried')}
                  {side(r.cur, r.runningDays, 'turnedAway')}
                  {side(r.cur, r.runningDays, 'fixed')}
                  {side(r.cur, r.runningDays, 'variable')}
                  {side(r.cur, r.runningDays, 'profit')}
                  {side(r.mod, r.runningDays, 'turns')}
                  {side(r.mod, r.runningDays, 'locos')}
                  {side(r.mod, r.runningDays, 'carried')}
                  {side(r.mod, r.runningDays, 'turnedAway')}
                  {side(r.mod, r.runningDays, 'fixed')}
                  {side(r.mod, r.runningDays, 'variable')}
                  {side(r.mod, r.runningDays, 'profit')}
                  <td className={`num sh-mono ${d == null ? 'sh-dim' : d >= 0 ? 'sh-delta--good' : 'sh-delta--bad'}`}>
                    {d == null ? '—' : `${d >= 0 ? '+' : '−'}${gbpK(Math.abs(d))}`}
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr><td colSpan={17} className="sh-dim">No slices in the programme yet — add this month on the Calendar stage, design it, and it appears here.</td></tr>
            )}
          </tbody>
        </table>
        {rows.some((r) => (r.mod?.noServiceDays ?? r.cur?.noServiceDays ?? 0) > 0 || (r.mod?.averageDays ?? r.cur?.averageDays ?? 0) > 0) && (
          <p className="stu-note" style={{ marginTop: 6 }}>
            Day for day, passengers are admissions (people, counted once), both sides.
            {rows.filter((r) => (r.mod?.noServiceDays ?? r.cur?.noServiceDays ?? 0) > 0).map((r) => ` ${r.monthLabel}: ${r.mod?.noServiceDays ?? r.cur?.noServiceDays} date(s) whose like-for-like day ran no standard services (0 services, fixed cost only).`).join('')}
            {rows.filter((r) => (r.mod?.averageDays ?? r.cur?.averageDays ?? 0) > 0).map((r) => ` ${r.monthLabel}: ${r.mod?.averageDays ?? r.cur?.averageDays} date(s) with no like-for-like day, at the day-type average.`).join('')}
          </p>
        )}
      </div>
      {/* The explanations sit behind one turn-down so the numbers are what is
          read first (Chris, 2026-10-28: "incredibly verbose"). */}
      <details className="sh-explain">
        <summary>How these figures are made</summary>
        <div style={{ padding: '6px 12px 0', fontSize: '0.74rem', color: '#8a8a8a' }}>
        Every month is modelled <strong>day by day</strong>: each running day (from thebigplan&apos;s timetable calendar
        — the source of the Run days column) is scored on its own timetable colour, its own day-type sales, its own
        capacity formation and its own paths, then banked; days sharing a colour and day-type are identical, so each
        combination is designed once. A range like 8–13 turns a day means the month runs more than one pattern. The current
        side is scored for every month; the modelled side fills in as you design each month&apos;s slice.
        <strong>Fixed</strong> is the P&amp;L&apos;s fixed lines for THAT month — the line runs all year, so a month with no
        standard services carries them with nothing else; the sliders on the Cost model stage are how they flex.
        <strong>Variable</strong> is the cost of running the trains: per running day, per turn, per train-mile, per passenger.
      </div>
        <div style={{ padding: '0 12px' }}>
        {rows.some((r) => r.mod || r.cur) && (
          <p className="stu-note" style={{ marginTop: 6 }}>
            <strong>Turned away</strong> = passengers turned away by timing: people in the like day&apos;s demand who did not travel on that side&apos;s
            timetable — no train within 75 minutes of their time once the Recapture discount applies, or the nearest train had no way home. They are
            not in Carried. A month showing — for this was saved before the figure existed; press ★ Build Model.
          </p>
        )}
        </div>
      <div className="sh-card-foot">
        Every month of the season is listed, running or not: a month with no standard services carries its fixed cost and no variable cost, on both sides.
        A month's modelled side appears once its slice is designed and saved; the current side is scored against that month's own timetable pattern.
        Locos = engine diagrams per operating day (steam + diesel/railcar); the season "up to N locos" is the largest day, i.e. the fleet the plan needs.
      </div>
      </details>
    </section>
  )
}
